import { useState, useEffect, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { ParticipantLayout } from "../../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
  FileText,
  Mail,
  User,
  Sparkles,
  Wand2,
  Minimize2,
  Maximize2,
  Target,
  Save,
  Image,
  Trash2,
  Plus,
  X,
  Loader2,
  Download,
  CheckCircle,
  Eye,
  Printer,
  Palette
} from "lucide-react";
import axios from "axios";
import { A4Document, TEMPLATE_OPTIONS } from "../../components/A4Preview";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AIButton = ({ icon: Icon, label, onClick, loading, disabled }) => (
  <Button
    variant="outline"
    size="sm"
    onClick={onClick}
    disabled={loading || disabled}
    className="gap-1.5 text-xs border-primary/30 hover:border-primary hover:bg-primary/10"
    data-testid={`ai-btn-${label.toLowerCase().replace(/\s/g, '-')}`}
  >
    {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Icon className="h-3 w-3" />}
    {label}
  </Button>
);

const BewerbungsTool = () => {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState("deckblatt");
  const [selectedTemplate, setSelectedTemplate] = useState("modern");
  
  // AI Dialog state
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [currentField, setCurrentField] = useState(null);
  const [jobAdDialogOpen, setJobAdDialogOpen] = useState(false);
  const [jobAdText, setJobAdText] = useState("");
  
  // Application data state
  const [appData, setAppData] = useState({
    // Deckblatt
    cover_title: "Bewerbung",
    cover_position: "",
    cover_company: "",
    cover_photo: null,
    // Personal
    personal_name: "",
    personal_address: "",
    personal_phone: "",
    personal_email: "",
    personal_birthdate: "",
    personal_birthplace: "",
    personal_nationality: "",
    // Anschreiben
    letter_recipient_name: "",
    letter_recipient_company: "",
    letter_recipient_address: "",
    letter_subject: "",
    letter_content: "",
    letter_date: new Date().toISOString().split("T")[0],
    // Lebenslauf
    cv_education: [],
    cv_experience: [],
    cv_skills: [],
    cv_languages: [],
    cv_hobbies: ""
  });

  // Load application data on mount
  useEffect(() => {
    loadApplicationData();
  }, []);

  const loadApplicationData = async () => {
    try {
      const res = await axios.get(`${API}/application`);
      setAppData(prev => ({
        ...prev,
        ...res.data,
        cv_education: res.data.cv_education || [],
        cv_experience: res.data.cv_experience || [],
        cv_skills: res.data.cv_skills || [],
        cv_languages: res.data.cv_languages || []
      }));
    } catch (error) {
      console.error("Load error:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveApplicationData = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/application`, appData);
      toast.success("Bewerbung gespeichert!");
    } catch (error) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const downloadPDF = async () => {
    try {
      toast.info("PDF wird erstellt...");
      const response = await axios.get(`${API}/application/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bewerbung-${appData.personal_name || 'dokument'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF heruntergeladen!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim PDF-Export");
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await axios.post(`${API}/application/photo`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setAppData(prev => ({ ...prev, cover_photo: res.data.photo }));
      toast.success("Foto hochgeladen!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Hochladen");
    }
  };

  const handleDeletePhoto = async () => {
    try {
      await axios.delete(`${API}/application/photo`);
      setAppData(prev => ({ ...prev, cover_photo: null }));
      toast.success("Foto gelöscht!");
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const requestAISuggestion = async (text, action, field, context = null) => {
    if (!text || text.trim().length < 10) {
      toast.error("Text zu kurz (min. 10 Zeichen)");
      return;
    }

    // For "adapt" action, show job ad dialog first
    if (action === "adapt" && !jobAdText) {
      setCurrentField({ text, field, context });
      setJobAdDialogOpen(true);
      return;
    }

    setAiLoading(true);
    setCurrentField({ text, field, context });

    try {
      const res = await axios.post(`${API}/application/ai/improve`, {
        text,
        action,
        job_ad: action === "adapt" ? jobAdText : null,
        context
      });
      setAiSuggestion(res.data);
      setAiDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "KI-Fehler");
    } finally {
      setAiLoading(false);
    }
  };

  const applyAISuggestion = () => {
    if (aiSuggestion && currentField) {
      setAppData(prev => ({
        ...prev,
        [currentField.field]: aiSuggestion.suggestion
      }));
      setAiDialogOpen(false);
      setAiSuggestion(null);
      toast.success("Vorschlag übernommen!");
    }
  };

  const handleJobAdSubmit = () => {
    setJobAdDialogOpen(false);
    if (currentField) {
      requestAISuggestion(currentField.text, "adapt", currentField.field, currentField.context);
    }
  };


  // AI Complete Generation
  const generateCompleteLetter = async () => {
    if (!appData.cover_position && !appData.letter_subject) {
      toast.error("Bitte zuerst Stelle/Position und Unternehmen angeben");
      return;
    }
    setAiGenerating(true);
    try {
      const res = await axios.post(`${API}/ai/cover-letter`, {
        job_title: appData.cover_position || appData.letter_subject || "",
        company_name: appData.cover_company || appData.letter_recipient_company || "",
        applicant_name: appData.personal_name || user?.name || "",
        applicant_qualifications: [
          appData.cv_education?.map(e => `${e.degree} (${e.institution})`).join(", "),
          appData.cv_skills?.map(s => s.name).join(", "),
          appData.cv_experience?.map(e => `${e.title} bei ${e.company}`).join(", ")
        ].filter(Boolean).join(". ") || "Industriemeister-Qualifikation",
        job_description: jobAdText || "",
        tone: "professionell"
      });
      setAppData(prev => ({ ...prev, letter_content: res.data.content }));
      toast.success("Anschreiben wurde von der KI generiert!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler bei der KI-Generierung");
    } finally {
      setAiGenerating(false);
    }
  };

  const generateCompleteCV = async () => {
    if (!appData.personal_name) {
      toast.error("Bitte zuerst den Namen unter 'Persönliche Daten' angeben");
      return;
    }
    setAiGenerating(true);
    try {
      const res = await axios.post(`${API}/ai/resume`, {
        full_name: appData.personal_name || "",
        contact_info: [appData.personal_phone, appData.personal_email, appData.personal_address].filter(Boolean).join(", "),
        education: appData.cv_education?.map(e => `${e.period}: ${e.degree} bei ${e.institution} - ${e.details}`).join("\n") || "Nicht angegeben",
        work_experience: appData.cv_experience?.map(e => `${e.period}: ${e.title} bei ${e.company} - ${e.description}`).join("\n") || "Nicht angegeben",
        skills: appData.cv_skills?.map(s => `${s.name} (${s.level})`).join(", ") || "Nicht angegeben",
        certifications: "",
        languages: appData.cv_languages?.map(l => `${l.name} (${l.level})`).join(", ") || "",
        hobbies: appData.cv_hobbies || ""
      });
      setAiSuggestion({ suggestion: res.data.content, original_text: "", action: "cv_generate" });
      setAiDialogOpen(true);
      toast.success("Lebenslauf-Vorschlag wurde generiert!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler bei der KI-Generierung");
    } finally {
      setAiGenerating(false);
    }
  };

  // CV Education handlers
  const addEducation = () => {
    setAppData(prev => ({
      ...prev,
      cv_education: [...prev.cv_education, { period: "", institution: "", degree: "", details: "" }]
    }));
  };

  const updateEducation = (index, field, value) => {
    setAppData(prev => ({
      ...prev,
      cv_education: prev.cv_education.map((edu, i) => 
        i === index ? { ...edu, [field]: value } : edu
      )
    }));
  };

  const removeEducation = (index) => {
    setAppData(prev => ({
      ...prev,
      cv_education: prev.cv_education.filter((_, i) => i !== index)
    }));
  };

  // CV Experience handlers
  const addExperience = () => {
    setAppData(prev => ({
      ...prev,
      cv_experience: [...prev.cv_experience, { period: "", company: "", position: "", tasks: "" }]
    }));
  };

  const updateExperience = (index, field, value) => {
    setAppData(prev => ({
      ...prev,
      cv_experience: prev.cv_experience.map((exp, i) => 
        i === index ? { ...exp, [field]: value } : exp
      )
    }));
  };

  const removeExperience = (index) => {
    setAppData(prev => ({
      ...prev,
      cv_experience: prev.cv_experience.filter((_, i) => i !== index)
    }));
  };

  // Skills handlers
  const [newSkill, setNewSkill] = useState("");
  const addSkill = () => {
    if (newSkill.trim()) {
      setAppData(prev => ({
        ...prev,
        cv_skills: [...prev.cv_skills, newSkill.trim()]
      }));
      setNewSkill("");
    }
  };

  const removeSkill = (index) => {
    setAppData(prev => ({
      ...prev,
      cv_skills: prev.cv_skills.filter((_, i) => i !== index)
    }));
  };

  // Languages handlers
  const addLanguage = () => {
    setAppData(prev => ({
      ...prev,
      cv_languages: [...prev.cv_languages, { language: "", level: "" }]
    }));
  };

  const updateLanguage = (index, field, value) => {
    setAppData(prev => ({
      ...prev,
      cv_languages: prev.cv_languages.map((lang, i) => 
        i === index ? { ...lang, [field]: value } : lang
      )
    }));
  };

  const removeLanguage = (index) => {
    setAppData(prev => ({
      ...prev,
      cv_languages: prev.cv_languages.filter((_, i) => i !== index)
    }));
  };

  // AI Buttons Component for text fields
  const AIButtons = ({ text, field, context }) => (
    <div className="flex flex-wrap gap-2 mt-2">
      <AIButton 
        icon={Sparkles} 
        label="Verbessern" 
        onClick={() => requestAISuggestion(text, "improve", field, context)}
        loading={aiLoading && currentField?.field === field}
        disabled={!text || text.length < 10}
      />
      <AIButton 
        icon={Wand2} 
        label="Professioneller" 
        onClick={() => requestAISuggestion(text, "professional", field, context)}
        loading={aiLoading && currentField?.field === field}
        disabled={!text || text.length < 10}
      />
      <AIButton 
        icon={Minimize2} 
        label="Kürzen" 
        onClick={() => requestAISuggestion(text, "shorten", field, context)}
        loading={aiLoading && currentField?.field === field}
        disabled={!text || text.length < 10}
      />
      <AIButton 
        icon={Maximize2} 
        label="Erweitern" 
        onClick={() => requestAISuggestion(text, "expand", field, context)}
        loading={aiLoading && currentField?.field === field}
        disabled={!text || text.length < 10}
      />
      <AIButton 
        icon={Target} 
        label="An Stelle anpassen" 
        onClick={() => requestAISuggestion(text, "adapt", field, context)}
        loading={aiLoading && currentField?.field === field}
        disabled={!text || text.length < 10}
      />
    </div>
  );

  if (loading) {
    return (
      <ParticipantLayout title="Bewerbungs-Tool">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </ParticipantLayout>
    );
  }

  const handlePrintA4 = () => {
    // The .print-target wrapper is rendered around the A4Document
    // in the Vorschau tab. CSS @media print hides everything else.
    setActiveTab("vorschau");
    setTimeout(() => window.print(), 250);
  };

  return (
    <ParticipantLayout title="Bewerbungs-Tool">
      <div className="space-y-6" data-testid="bewerbungs-tool">
        {/* Header with Save Button */}
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  KI-gestütztes Bewerbungs-Tool
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Erstellen Sie professionelle Bewerbungsunterlagen mit KI-Unterstützung
                </p>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={downloadPDF}
                  className="gap-2"
                  data-testid="download-pdf-btn"
                >
                  <Download className="h-4 w-4" />
                  <span className="hidden sm:inline">PDF</span>
                </Button>
                <Button 
                  onClick={saveApplicationData} 
                  disabled={saving}
                  className="gap-2"
                  data-testid="save-application-btn"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Speichern
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-4 bg-secondary/50">
            <TabsTrigger value="deckblatt" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Image className="h-4 w-4" />
              <span className="hidden sm:inline">Deckblatt</span>
            </TabsTrigger>
            <TabsTrigger value="anschreiben" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Mail className="h-4 w-4" />
              <span className="hidden sm:inline">Anschreiben</span>
            </TabsTrigger>
            <TabsTrigger value="lebenslauf" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <User className="h-4 w-4" />
              <span className="hidden sm:inline">Lebenslauf</span>
            </TabsTrigger>
            <TabsTrigger value="vorschau" data-testid="tab-vorschau" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Eye className="h-4 w-4" />
              <span className="hidden sm:inline">Vorschau & Design</span>
            </TabsTrigger>
          </TabsList>

          {/* DECKBLATT TAB */}
          <TabsContent value="deckblatt" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Photo Upload */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Image className="h-5 w-5 text-primary" />
                    Bewerbungsfoto
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-col items-center gap-4">
                    {appData.cover_photo ? (
                      <div className="relative">
                        <img 
                          src={appData.cover_photo} 
                          alt="Bewerbungsfoto" 
                          className="w-40 h-52 object-cover rounded-lg border border-border"
                          data-testid="cover-photo-preview"
                        />
                        <Button
                          variant="destructive"
                          size="icon"
                          className="absolute -top-2 -right-2 h-8 w-8"
                          onClick={handleDeletePhoto}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="w-40 h-52 bg-secondary/50 rounded-lg border-2 border-dashed border-border flex items-center justify-center">
                        <User className="h-16 w-16 text-muted-foreground/50" />
                      </div>
                    )}
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handlePhotoUpload}
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                    />
                    <Button 
                      variant="outline" 
                      onClick={() => fileInputRef.current?.click()}
                      className="gap-2"
                      data-testid="upload-photo-btn"
                    >
                      <Image className="h-4 w-4" />
                      {appData.cover_photo ? "Foto ändern" : "Foto hochladen"}
                    </Button>
                    <p className="text-xs text-muted-foreground text-center">
                      Max. 5MB, JPEG/PNG/WebP
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Cover Details */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-lg">Deckblatt-Informationen</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Titel</Label>
                    <Input
                      value={appData.cover_title}
                      onChange={(e) => setAppData(prev => ({ ...prev, cover_title: e.target.value }))}
                      placeholder="Bewerbung"
                      className="bg-background"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Position / Stelle</Label>
                    <Input
                      value={appData.cover_position}
                      onChange={(e) => setAppData(prev => ({ ...prev, cover_position: e.target.value }))}
                      placeholder="z.B. Industriemeister Metall"
                      className="bg-background"
                      data-testid="cover-position-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Unternehmen</Label>
                    <Input
                      value={appData.cover_company}
                      onChange={(e) => setAppData(prev => ({ ...prev, cover_company: e.target.value }))}
                      placeholder="Name des Unternehmens"
                      className="bg-background"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Personal Data */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="h-5 w-5 text-primary" />
                  Persönliche Daten
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Vollständiger Name</Label>
                  <Input
                    value={appData.personal_name}
                    onChange={(e) => setAppData(prev => ({ ...prev, personal_name: e.target.value }))}
                    placeholder="Max Mustermann"
                    className="bg-background"
                    data-testid="personal-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Adresse</Label>
                  <Input
                    value={appData.personal_address}
                    onChange={(e) => setAppData(prev => ({ ...prev, personal_address: e.target.value }))}
                    placeholder="Musterstraße 1, 12345 Stadt"
                    className="bg-background"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefon</Label>
                  <Input
                    value={appData.personal_phone}
                    onChange={(e) => setAppData(prev => ({ ...prev, personal_phone: e.target.value }))}
                    placeholder="+49 123 456789"
                    className="bg-background"
                  />
                </div>
                <div className="space-y-2">
                  <Label>E-Mail</Label>
                  <Input
                    value={appData.personal_email}
                    onChange={(e) => setAppData(prev => ({ ...prev, personal_email: e.target.value }))}
                    placeholder="max@example.com"
                    className="bg-background"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Geburtsdatum</Label>
                  <Input
                    type="date"
                    value={appData.personal_birthdate}
                    onChange={(e) => setAppData(prev => ({ ...prev, personal_birthdate: e.target.value }))}
                    className="bg-background"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Geburtsort</Label>
                  <Input
                    value={appData.personal_birthplace}
                    onChange={(e) => setAppData(prev => ({ ...prev, personal_birthplace: e.target.value }))}
                    placeholder="München"
                    className="bg-background"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Staatsangehörigkeit</Label>
                  <Input
                    value={appData.personal_nationality}
                    onChange={(e) => setAppData(prev => ({ ...prev, personal_nationality: e.target.value }))}
                    placeholder="Deutsch"
                    className="bg-background"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ANSCHREIBEN TAB */}
          <TabsContent value="anschreiben" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Recipient */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-lg">Empfänger</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Ansprechpartner</Label>
                    <Input
                      value={appData.letter_recipient_name}
                      onChange={(e) => setAppData(prev => ({ ...prev, letter_recipient_name: e.target.value }))}
                      placeholder="Frau Schmidt"
                      className="bg-background"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Unternehmen</Label>
                    <Input
                      value={appData.letter_recipient_company}
                      onChange={(e) => setAppData(prev => ({ ...prev, letter_recipient_company: e.target.value }))}
                      placeholder="Firma GmbH"
                      className="bg-background"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Adresse</Label>
                    <Textarea
                      value={appData.letter_recipient_address}
                      onChange={(e) => setAppData(prev => ({ ...prev, letter_recipient_address: e.target.value }))}
                      placeholder="Firmenstraße 1&#10;12345 Stadt"
                      className="bg-background resize-none"
                      rows={3}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Letter Details */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-lg">Brief-Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Datum</Label>
                    <Input
                      type="date"
                      value={appData.letter_date}
                      onChange={(e) => setAppData(prev => ({ ...prev, letter_date: e.target.value }))}
                      className="bg-background"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Betreff</Label>
                    <Input
                      value={appData.letter_subject}
                      onChange={(e) => setAppData(prev => ({ ...prev, letter_subject: e.target.value }))}
                      placeholder="Bewerbung als Industriemeister Metall"
                      className="bg-background"
                      data-testid="letter-subject-input"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Letter Content */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Mail className="h-5 w-5 text-primary" />
                  Anschreiben-Text
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  onClick={generateCompleteLetter}
                  disabled={aiGenerating}
                  variant="outline"
                  className="w-full gap-2 border-primary/30 hover:bg-primary/10"
                  data-testid="generate-complete-letter"
                >
                  {aiGenerating ? (
                    <><Wand2 className="h-4 w-4 animate-spin" /> KI generiert Anschreiben...</>
                  ) : (
                    <><Sparkles className="h-4 w-4 text-primary" /> Komplettes Anschreiben per KI generieren</>
                  )}
                </Button>
                <div className="space-y-2">
                  <Label>Ihr Anschreiben</Label>
                  <Textarea
                    value={appData.letter_content}
                    onChange={(e) => setAppData(prev => ({ ...prev, letter_content: e.target.value }))}
                    placeholder="Sehr geehrte Frau Schmidt,&#10;&#10;mit großem Interesse habe ich Ihre Stellenanzeige gelesen...&#10;&#10;Mit freundlichen Grüßen"
                    className="bg-background resize-none min-h-[300px]"
                    data-testid="letter-content-textarea"
                  />
                  <AIButtons 
                    text={appData.letter_content} 
                    field="letter_content" 
                    context="Anschreiben für eine Bewerbung"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* LEBENSLAUF TAB */}
          <TabsContent value="lebenslauf" className="space-y-4">
            {/* AI Generate CV Button */}
            <Card className="bg-card border-border">
              <CardContent className="pt-6">
                <Button
                  onClick={generateCompleteCV}
                  disabled={aiGenerating}
                  variant="outline"
                  className="w-full gap-2 border-primary/30 hover:bg-primary/10"
                  data-testid="generate-complete-cv"
                >
                  {aiGenerating ? (
                    <><Wand2 className="h-4 w-4 animate-spin" /> KI generiert Lebenslauf...</>
                  ) : (
                    <><Sparkles className="h-4 w-4 text-primary" /> Lebenslauf-Vorschlag per KI generieren</>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground text-center mt-2">
                  Die KI erstellt einen Vorschlag basierend auf Ihren eingegebenen Daten.
                </p>
              </CardContent>
            </Card>
            {/* Education */}
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Ausbildung / Studium</CardTitle>
                <Button variant="outline" size="sm" onClick={addEducation} className="gap-1">
                  <Plus className="h-4 w-4" />
                  Hinzufügen
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {appData.cv_education.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">
                    Noch keine Ausbildung hinzugefügt
                  </p>
                ) : (
                  appData.cv_education.map((edu, index) => (
                    <div key={index} className="p-4 bg-secondary/30 rounded-lg space-y-3 relative">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute top-2 right-2 h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => removeEducation(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pr-10">
                        <div className="space-y-1">
                          <Label className="text-xs">Zeitraum</Label>
                          <Input
                            value={edu.period}
                            onChange={(e) => updateEducation(index, "period", e.target.value)}
                            placeholder="09/2015 - 07/2019"
                            className="bg-background"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Institution</Label>
                          <Input
                            value={edu.institution}
                            onChange={(e) => updateEducation(index, "institution", e.target.value)}
                            placeholder="Technische Universität München"
                            className="bg-background"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Abschluss</Label>
                          <Input
                            value={edu.degree}
                            onChange={(e) => updateEducation(index, "degree", e.target.value)}
                            placeholder="Bachelor of Engineering"
                            className="bg-background"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Details</Label>
                          <Input
                            value={edu.details}
                            onChange={(e) => updateEducation(index, "details", e.target.value)}
                            placeholder="Schwerpunkt: Produktionstechnik"
                            className="bg-background"
                          />
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Experience */}
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Berufserfahrung</CardTitle>
                <Button variant="outline" size="sm" onClick={addExperience} className="gap-1">
                  <Plus className="h-4 w-4" />
                  Hinzufügen
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {appData.cv_experience.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">
                    Noch keine Berufserfahrung hinzugefügt
                  </p>
                ) : (
                  appData.cv_experience.map((exp, index) => (
                    <div key={index} className="p-4 bg-secondary/30 rounded-lg space-y-3 relative">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute top-2 right-2 h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => removeExperience(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pr-10">
                        <div className="space-y-1">
                          <Label className="text-xs">Zeitraum</Label>
                          <Input
                            value={exp.period}
                            onChange={(e) => updateExperience(index, "period", e.target.value)}
                            placeholder="01/2020 - heute"
                            className="bg-background"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Unternehmen</Label>
                          <Input
                            value={exp.company}
                            onChange={(e) => updateExperience(index, "company", e.target.value)}
                            placeholder="BMW AG"
                            className="bg-background"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Position</Label>
                          <Input
                            value={exp.position}
                            onChange={(e) => updateExperience(index, "position", e.target.value)}
                            placeholder="Produktionsleiter"
                            className="bg-background"
                          />
                        </div>
                        <div className="space-y-1 md:col-span-2">
                          <Label className="text-xs">Aufgaben</Label>
                          <Textarea
                            value={exp.tasks}
                            onChange={(e) => updateExperience(index, "tasks", e.target.value)}
                            placeholder="Leitung eines Teams von 15 Mitarbeitern, Optimierung der Produktionsprozesse..."
                            className="bg-background resize-none"
                            rows={2}
                          />
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Skills */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg">Kenntnisse & Fähigkeiten</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={newSkill}
                    onChange={(e) => setNewSkill(e.target.value)}
                    placeholder="z.B. SAP, MS Office, Lean Management"
                    className="bg-background"
                    onKeyPress={(e) => e.key === "Enter" && addSkill()}
                  />
                  <Button variant="outline" onClick={addSkill}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {appData.cv_skills.map((skill, index) => (
                    <span 
                      key={index} 
                      className="px-3 py-1 bg-primary/20 text-primary rounded-full text-sm flex items-center gap-2"
                    >
                      {skill}
                      <button onClick={() => removeSkill(index)} className="hover:text-destructive">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Languages */}
            <Card className="bg-card border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Sprachkenntnisse</CardTitle>
                <Button variant="outline" size="sm" onClick={addLanguage} className="gap-1">
                  <Plus className="h-4 w-4" />
                  Hinzufügen
                </Button>
              </CardHeader>
              <CardContent className="space-y-3">
                {appData.cv_languages.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">
                    Noch keine Sprachen hinzugefügt
                  </p>
                ) : (
                  appData.cv_languages.map((lang, index) => (
                    <div key={index} className="flex gap-3 items-center">
                      <Input
                        value={lang.language}
                        onChange={(e) => updateLanguage(index, "language", e.target.value)}
                        placeholder="Deutsch"
                        className="bg-background flex-1"
                      />
                      <Input
                        value={lang.level}
                        onChange={(e) => updateLanguage(index, "level", e.target.value)}
                        placeholder="Muttersprache / C1 / B2"
                        className="bg-background flex-1"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={() => removeLanguage(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Hobbies */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg">Hobbys & Interessen</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={appData.cv_hobbies}
                  onChange={(e) => setAppData(prev => ({ ...prev, cv_hobbies: e.target.value }))}
                  placeholder="Lesen, Wandern, Ehrenamtliche Arbeit im Sportverein"
                  className="bg-background resize-none"
                  rows={2}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* VORSCHAU & DESIGN TAB */}
          <TabsContent value="vorschau" className="space-y-4">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Palette className="h-5 w-5 text-primary" />
                  Design-Vorlage wählen
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {TEMPLATE_OPTIONS.map(tpl => (
                    <button
                      key={tpl.id}
                      onClick={() => setSelectedTemplate(tpl.id)}
                      data-testid={`template-${tpl.id}`}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        selectedTemplate === tpl.id
                          ? "border-primary bg-primary/10"
                          : "border-border bg-secondary/30 hover:border-primary/50"
                      }`}
                    >
                      <div className="font-semibold mb-1">{tpl.label}</div>
                      <div className="text-xs text-muted-foreground">{tpl.desc}</div>
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <Button
                    onClick={handlePrintA4}
                    className="gap-2"
                    data-testid="print-a4-btn"
                  >
                    <Printer className="h-4 w-4" />
                    Drucken / Als PDF speichern (A4)
                  </Button>
                  <Button
                    variant="outline"
                    onClick={downloadPDF}
                    className="gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Server-PDF (Standard)
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Tipp: Im Druck-Dialog "Als PDF speichern" wählen, um eine pixelgenaue A4-Bewerbung zu erhalten.
                </p>
              </CardContent>
            </Card>

            <div className="a4-viewer print-target" data-testid="a4-preview-container">
              <A4Document data={appData} template={selectedTemplate} />
            </div>
          </TabsContent>
        </Tabs>

        {/* AI Suggestion Dialog */}
        <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                KI-Vorschlag
              </DialogTitle>
              <DialogDescription>
                Überprüfen Sie den Vorschlag und übernehmen Sie ihn bei Bedarf.
              </DialogDescription>
            </DialogHeader>
            
            {aiSuggestion && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Originaltext:</Label>
                  <div className="p-3 bg-secondary/30 rounded-lg text-sm whitespace-pre-wrap max-h-32 overflow-y-auto">
                    {aiSuggestion.original_text}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-primary flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Verbesserter Text:
                  </Label>
                  <div className="p-3 bg-primary/10 border border-primary/30 rounded-lg text-sm whitespace-pre-wrap max-h-48 overflow-y-auto">
                    {aiSuggestion.suggestion}
                  </div>
                </div>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setAiDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button onClick={applyAISuggestion} className="gap-2" data-testid="apply-ai-suggestion-btn">
                <CheckCircle className="h-4 w-4" />
                Vorschlag übernehmen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Job Ad Input Dialog */}
        <Dialog open={jobAdDialogOpen} onOpenChange={setJobAdDialogOpen}>
          <DialogContent className="bg-card border-border max-w-xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Target className="h-5 w-5 text-primary" />
                Stellenanzeige eingeben
              </DialogTitle>
              <DialogDescription>
                Fügen Sie die Stellenanzeige ein, um Ihren Text darauf anzupassen.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <Textarea
                value={jobAdText}
                onChange={(e) => setJobAdText(e.target.value)}
                placeholder="Kopieren Sie hier die Stellenanzeige ein..."
                className="bg-background resize-none min-h-[200px]"
                data-testid="job-ad-textarea"
              />
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => setJobAdDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button 
                onClick={handleJobAdSubmit} 
                disabled={!jobAdText.trim()}
                className="gap-2"
              >
                <Target className="h-4 w-4" />
                Text anpassen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </ParticipantLayout>
  );
};

export default BewerbungsTool;
